import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FcCheckmark, FcCancel } from 'react-icons/fc';
import { FaCogs, FaGift } from 'react-icons/fa';
import { PropagateLoader } from 'react-spinners';

import BreadCrumb from '~/components/BreadCrumb';
import DefaultInput from '~/components/Inputs/Default';
import EditButton from '~/components/Buttons/Edit';
import DeleteButton from '~/components/Buttons/Delete';
import Pagination from '~/components/Pagination';
import NewButton from '~/components/Buttons/New';
import ConfirmDeleteDialog from '~/components/Dialogs/ConfirmDelete';

import { getFormattedCurrency } from '~/utils';

import { useToast } from '~/hooks/toast';

import api from '~/services/api';

import { Container, FilterContent, Table, Td, SymbolWrapper } from './styles';

interface IGiftType {
  id: string;
  name: string;
  erp_code: string;
  active: boolean;
  price: number;
}

const AMOUNT_PER_PAGE = 5;

const List: React.FC = () => {
  const { addToast } = useToast();
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [filter, setFilter] = useState('');
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [giftTypes, setGiftTypes] = useState<IGiftType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [giftTypeIdDelete, setGiftTypeIdDelete] = useState('');
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [titleDelete, setTitleDelete] = useState('');
  const [subTitleDelete, setSubTitleDelete] = useState('');

  useEffect(() => {
    const loadGiftTypes = async () => {
      await api
        .get(
          `/configurations/gifttypes?page=${page}&filter=${filter}&amount=${AMOUNT_PER_PAGE}`,
        )
        .then(response => {
          setGiftTypes(response.data[0]);
          setCount(response.data[1]);
          setLoading(false);
        });
    };

    loadGiftTypes();
  }, [page, filter, lastRefresh]);

  const onDelete = useCallback(
    async (id: string) => {
      try {
        await api.delete(`/configurations/gifttypes/${id}`).then(() => {
          setPage(0);
          setLastRefresh(new Date());

          addToast({
            type: 'success',
            title: 'Tipo de brinde excluído!',
            description: 'Tipo de brinde excluído com sucesso.',
          });
        });
      } catch (error: any) {
        addToast({
          type: 'error',
          title: 'Ocorreu um erro!',
          description: error.response.data.message,
        });
      }
      setGiftTypeIdDelete('');
      setTitleDelete('');
      setSubTitleDelete('');
    },
    [addToast],
  );

  return (
    <Container style={{ minHeight: 600 }}>
      <BreadCrumb
        main="Configurações"
        path="/configurations/main"
        firstChild="Tipos de Brindes"
        mainIcon={FaCogs}
        firstChildIcon={FaGift}
        mainColored
      />

      <FilterContent>
        <DefaultInput
          name="search"
          labelText="Pesquisar"
          type="text"
          onChange={event => {
            setFilter(event.target.value);
            setPage(0);
          }}
        />
      </FilterContent>

      <Table>
        <thead>
          <tr>
            <th style={{ width: '20%', textAlign: 'left' }}>Código ERP</th>
            <th style={{ width: '30%', textAlign: 'left' }}>Nome</th>
            <th style={{ width: '20%', textAlign: 'left' }}>Valor</th>
            <th style={{ width: '20%', textAlign: 'center' }}>Situação</th>
            <th style={{ width: '10%' }} />
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr>
              <Td colSpan={5} style={{ padding: '3rem', textAlign: 'center' }}>
                <PropagateLoader color="#276c8d" />
              </Td>
            </tr>
          ) : giftTypes.length > 0 ? (
            giftTypes.map(giftType => (
              <tr key={giftType.id}>
                <Td
                  style={{ width: '20%', textAlign: 'left' }}
                  heading="CÓDIGO ERP"
                >
                  {giftType.erp_code ? giftType.erp_code : ''}
                </Td>
                <Td style={{ width: '30%', textAlign: 'left' }} heading="NOME">
                  {giftType.name}
                </Td>
                <Td style={{ width: '20%', textAlign: 'left' }} heading="VALOR">
                  {getFormattedCurrency(giftType.price)}
                </Td>
                <Td
                  style={{ width: '20%', textAlign: 'center' }}
                  heading="SITUAÇÃO"
                >
                  <SymbolWrapper>
                    {giftType.active ? (
                      <FcCheckmark size={22} />
                    ) : (
                      <FcCancel size={22} />
                    )}
                  </SymbolWrapper>
                </Td>
                <Td
                  style={{ width: '10%', display: 'flex', textAlign: 'center' }}
                  heading={' '}
                >
                  <Link
                    to={{
                      pathname: `/configurations/gifttypes/edit/${giftType.id}`,
                      state: giftType,
                    }}
                  >
                    <EditButton />
                  </Link>

                  <DeleteButton
                    style={{ marginLeft: '0.5rem' }}
                    onClick={() => {
                      setGiftTypeIdDelete(giftType.id);
                      setIsOpenDelete(true);
                      setTitleDelete(
                        `Deseja realmente excluir o tipo de brinde ${giftType.name}?`,
                      );
                      setSubTitleDelete(
                        'Esta operação não poderá ser desfeita!',
                      );
                    }}
                  />
                </Td>
              </tr>
            ))
          ) : (
            <tr>
              <Td colSpan={5} style={{ textAlign: 'center' }}>
                Nenhuma informação disponível
              </Td>
            </tr>
          )}
        </tbody>
      </Table>

      <div style={{ display: 'flex', marginTop: '2rem' }}>
        <Pagination
          page={page}
          count={count}
          amountPerPage={AMOUNT_PER_PAGE}
          setPage={setPage}
        />

        <Link to="/configurations/gifttypes/new">
          <NewButton />
        </Link>
      </div>

      <ConfirmDeleteDialog
        isOpen={isOpenDelete}
        title={titleDelete}
        subTitle={subTitleDelete}
        setIsOpen={setIsOpenDelete}
        onClose={() => {
          onDelete(giftTypeIdDelete);
        }}
      />
    </Container>
  );
};

export default List;
